// "Sticker" drawing primitives — the in-game counterpart of the lobby icon
// language in gameIcons.ts (bold flat fills, thick ink #1B1534 outlines — see theme.ts), with a
// single consistent light source so shapes read as solid instead of flat.
//
// Keep these cheap: the drop shadow is an offset dark copy, never `shadowBlur`.

export const OUTLINE = "#1B1534";
/** light comes from the top-left */
export const LIGHT = { x: -0.55, y: -0.8 };

export interface StickerOpts {
  fill: string | CanvasGradient;
  outline?: string;
  outlineW?: number;
  shadow?: boolean;
  shadowColour?: string;
  shadowOffset?: number;
  /** draw a soft top highlight stroke inside the shape */
  highlight?: string | false;
}

/**
 * Run `path` (which should issue beginPath + the shape) three times: an offset
 * dark drop shadow, the fill, then the thick outline. `path` must not fill or
 * stroke itself.
 */
export function sticker(
  g: CanvasRenderingContext2D,
  path: () => void,
  o: StickerOpts,
) {
  const ow = o.outlineW ?? 2.4;
  if (o.shadow !== false) {
    const off = o.shadowOffset ?? 3;
    g.save();
    g.translate(off * -LIGHT.x, off * -LIGHT.y);
    g.fillStyle = o.shadowColour ?? "rgba(27,21,52,0.9)";
    path();
    g.fill();
    g.restore();
  }
  g.fillStyle = o.fill;
  path();
  g.fill();
  if (o.highlight) {
    g.save();
    path();
    g.clip();
    g.strokeStyle = o.highlight;
    g.lineWidth = ow * 2.2;
    g.globalAlpha = 0.55;
    g.translate(LIGHT.x * 3, LIGHT.y * 3);
    path();
    g.stroke();
    g.restore();
  }
  g.strokeStyle = o.outline ?? OUTLINE;
  g.lineWidth = ow;
  g.lineJoin = "round";
  g.lineCap = "round";
  path();
  g.stroke();
}

export function roundRectPath(
  g: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}

/** a bevelled tile: fill, top-left light edge, bottom-right dark edge, outline */
export function bevelRect(
  g: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
  fill: string,
  opts: { outline?: string; outlineW?: number; inset?: number } = {},
) {
  const inset = opts.inset ?? Math.max(2, Math.min(w, h) * 0.12);
  roundRectPath(g, x, y, w, h, r);
  g.fillStyle = fill;
  g.fill();
  roundRectPath(g, x + inset * 0.4, y + inset * 0.4, w - inset * 0.8, h - inset * 0.8, r * 0.7);
  g.fillStyle = "rgba(255,255,255,0.16)";
  g.fill();
  roundRectPath(g, x + inset, y + inset, w - inset * 2, h - inset * 2, r * 0.55);
  g.fillStyle = fill;
  g.fill();
  roundRectPath(g, x, y, w, h, r);
  g.strokeStyle = opts.outline ?? OUTLINE;
  g.lineWidth = opts.outlineW ?? 2.2;
  g.stroke();
}

/** a cheap shaded sphere — solid fill + a light disc offset toward the light */
export function puck(
  g: CanvasRenderingContext2D,
  x: number, y: number, r: number,
  colour: string,
  outline: string | false = OUTLINE,
) {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = colour;
  g.fill();
  g.beginPath();
  g.arc(x + LIGHT.x * r * 0.35, y + LIGHT.y * r * 0.35, r * 0.62, 0, Math.PI * 2);
  g.fillStyle = "rgba(255,255,255,0.28)";
  g.fill();
  g.beginPath();
  g.arc(x + LIGHT.x * r * 0.5, y + LIGHT.y * r * 0.5, r * 0.26, 0, Math.PI * 2);
  g.fillStyle = "rgba(255,255,255,0.5)";
  g.fill();
  if (outline) {
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.strokeStyle = outline;
    g.lineWidth = 2.2;
    g.stroke();
  }
}

/** an elliptical ground shadow (for things with fake height) */
export function groundShadow(
  g: CanvasRenderingContext2D,
  x: number, y: number, rx: number, ry: number, alpha = 0.3,
) {
  g.save();
  g.translate(x, y);
  g.scale(1, ry / rx);
  g.beginPath();
  g.arc(0, 0, rx, 0, Math.PI * 2);
  g.fillStyle = `rgba(27,21,52,${alpha})`;
  g.fill();
  g.restore();
}

const gradCache = new Map<string, CanvasGradient>();
/** memoise a gradient by key so it isn't rebuilt every frame */
export function cachedGrad(
  key: string,
  make: () => CanvasGradient,
): CanvasGradient {
  let gd = gradCache.get(key);
  if (!gd) { gd = make(); gradCache.set(key, gd); }
  return gd;
}
export function clearGradCache() { gradCache.clear(); }
